import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { Router } from '@angular/router';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { untilComponentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { GlobalSearchService } from 'app/services/global-search.service';
import { Dictionary } from 'app/shared/models';
import { SearchResultHighlight, SearchResultItem } from 'app/shared/models/global-search';
import { typeTitleMap } from 'app/shared/models/type-title-map';
import { RelatedToType } from 'app/shared/enums';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { FieldDef } from '@core/models';
import { plural } from 'pluralize';
import { SearchModule } from 'app/layout/common/search/search.module';
import { TsButtonModule } from 'tsui';

type SearchResultRow = { item: SearchResultItem; idx: number };
type SearchResultGroup = { relatedToType: RelatedToType; label: string; items: SearchResultRow[] };

@Component({
  selector: 'shell-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    SearchModule,
    TsButtonModule,
  ],
  templateUrl: './shell-search-modal.component.html',
  styleUrl: './shell-search-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellSearchModalComponent extends OnDestroyMixin implements AfterViewInit {
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  minLength = 2;
  debounceMs = 400;
  searchControl = new UntypedFormControl('');
  ui = signal({ isLoading: false });
  results: SearchResultItem[] = [];
  groupedResults: SearchResultGroup[] = [];
  selectedIndex: number | null = null;

  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly matDialogRef = inject(MatDialogRef<ShellSearchModalComponent>);
  private readonly cdRef = inject(ChangeDetectorRef);

  private fieldDefs: FieldDef[] = [];
  private accountId = Number(this.store.selectSnapshot(AccountState.getAccount)?.id);

  ngAfterViewInit(): void {
    setTimeout(() => this.searchInput()?.nativeElement?.focus());

    this.store
      .select(ObjectsManagerState.getAllFields)
      .pipe(untilComponentDestroyed(this))
      .subscribe((defs) => {
        this.fieldDefs = defs;
        this.cdRef.markForCheck();
      });

    this.searchControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(this.debounceMs),
        tap((query: string) => {
          if (!query || query.length < this.minLength) {
            this.results = [];
            this.groupedResults = [];
            this.selectedIndex = null;
            this.ui.set({ isLoading: false });
            this.cdRef.markForCheck();
          }
        }),
        filter((query: string) => !!query && query.length >= this.minLength),
        tap(() => this.ui.set({ isLoading: true })),
        switchMap((query: string) =>
          this.globalSearchService.search({
            accountId: this.accountId,
            query,
          }),
        ),
        map((results) =>
          Object.keys(results)
            .reduce(
              (prev: any[], curr) => [...prev, ...results[curr].map((item) => ({ ...item, relatedToType: curr }))],
              [],
            )
            .map((item: any) => ({
              ...item,
              relatedToType: item.relatedToType as RelatedToType,
              link: this.jupiterLink(item),
              highlights: this.parseHighlights(item.search_highlights, item.relatedToType),
            })),
        ),
      )
      .subscribe((mappedResults) => {
        this.results = mappedResults || [];
        this.groupedResults = this.buildGroups(this.results);
        this.selectedIndex = this.results.length ? 0 : null;
        this.ui.set({ isLoading: false });
        this.cdRef.markForCheck();
      });
  }

  trackByGroupType(_: number, group: SearchResultGroup): string {
    return group.relatedToType;
  }

  trackByRowIdx(_: number, row: SearchResultRow): number {
    return row.idx;
  }

  onResultRowClicked(): void {
    this.matDialogRef.close();
  }

  onInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape': {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialogAndReset();
        return;
      }
      case 'ArrowDown': {
        event.preventDefault();
        this.moveSelection(1);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.moveSelection(-1);
        return;
      }
      case 'Enter': {
        if (this.selectedIndex === null || !this.results?.length) return;
        event.preventDefault();
        event.stopPropagation();
        this.openSelected();
        return;
      }
    }
  }

  private moveSelection(direction: 1 | -1): void {
    const len = this.results?.length || 0;
    if (!len) return;

    const current = this.selectedIndex ?? (direction === 1 ? -1 : len);
    const next = Math.min(len - 1, Math.max(0, current + direction));
    if (next === this.selectedIndex) return;

    this.selectedIndex = next;
    this.scrollToIndex(next);
    this.cdRef.markForCheck();
  }

  private scrollToIndex(idx: number): void {
    const el = document.getElementById(`search-row-${idx}`);
    el?.scrollIntoView({ block: 'nearest' });
  }

  private openSelected(): void {
    const selected = this.results[this.selectedIndex];
    if (!selected?.link) return;

    this.router.navigateByUrl(selected.link);
    this.matDialogRef.close();
  }

  closeDialogAndReset(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.results = [];
    this.groupedResults = [];
    this.selectedIndex = null;
    this.ui.update((prev) => ({ ...prev, isLoading: false }));
    this.matDialogRef.close();
  }

  private buildGroups(results: SearchResultItem[]): SearchResultGroup[] {
    const groups = new Map<RelatedToType, SearchResultGroup>();
    const ordered: SearchResultGroup[] = [];

    results.forEach((item, idx) => {
      const key = item.relatedToType;
      const existing = groups.get(key);
      if (existing) {
        existing.items.push({ item, idx });
        return;
      }

      const group: SearchResultGroup = {
        relatedToType: key,
        label: typeTitleMap[key] ?? key,
        items: [{ item, idx }],
      };
      groups.set(key, group);
      ordered.push(group);
    });

    return ordered;
  }

  private parseHighlights(value: Dictionary<any>, relatedToType: RelatedToType): SearchResultHighlight[] {
    if (!value) return [];

    return Object.keys(value).reduce((prev: SearchResultHighlight[], fieldName) => {
      const fieldDef = this.fieldDefs.find(
        ({ name, related_to_type }) => name === fieldName && related_to_type == relatedToType,
      );
      const label = fieldDef?.label ?? fieldName;

      return [
        ...prev,
        {
          label,
          content: String(value[fieldName]).replace(/<.*?>/g, ''),
        },
      ];
    }, []);
  }

  private jupiterLink(item: any): string {
    const urlKey = plural(item.relatedToType).toLowerCase();

    if ([RelatedToType.Contact, RelatedToType.Company].includes(item.relatedToType)) {
      return `/space/void?return=/space/${urlKey}/0/${item.id}`;
    }

    if (item.relatedToType === RelatedToType.Project) {
      const [, pipelineId] = item.link.split('/').reverse();
      return `/projects/${pipelineId}/${item.id}`;
    }

    if (item.relatedToType === RelatedToType.Property) {
      return `/space/void?return=/space/properties/0/${item.id}`;
    }

    if (item.relatedToType === RelatedToType.PropertyTenant) {
      return `/space/void?return=/space/leases/0/${item.id}`;
    }

    return item.link;
  }
}
